import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Stethoscope, FileText, CheckCircle2 } from "lucide-react";

const DONE_STATUSES = new Set([
  "completed",
  "paid_out",
  "sent_back",
  "declined_returning",
]);
// Diagnose-specific "completed" set — fixer considers the case done either
// when the customer declined or when the repair phase has fully completed.
const DIAG_COMPLETED_STATUSES = new Set([
  "declined_returning",
  "completed",
  "paid_out",
  "sent_back",
]);

const DIAG_STATUS_LABEL = {
  assigned: "Diagnose påbegynt",
  diagnosis_ready: "Diagnose sendt — venter på kunde",
  approved_awaiting_extra_payment: "Kunde godkjent — venter på betaling",
  declined_returning: "Kunde avslo",
  received: "Enhet mottatt",
  in_progress: "Under reparasjon",
  repair_done: "Reparasjon fullført",
  sent_back: "Sendt tilbake",
  completed: "Fullført",
  paid_out: "Utbetalt",
};

export default function FixerDashboard() {
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [diagDraft, setDiagDraft] = useState({});
  const [diagTab, setDiagTab] = useState("aktive");

  const refresh = useCallback(() => {
    api.get("/fixer/orders").then((r) => setOrders(r.data));
  }, []);

  useEffect(() => {
    api.get("/fixer/profile").then((r) => setProfile(r.data)).catch(() => {});
    refresh();
  }, [refresh]);

  const submitDiagnosis = async (o) => {
    const d = diagDraft[o.id] || {};
    if (!d.findings || !d.recommended_repair || !d.recommended_price) {
      toast.error("Fyll ut funn, anbefalt reparasjon og pris.");
      return;
    }
    try {
      await api.patch(`/fixer/orders/${o.id}/diagnosis`, {
        findings: d.findings,
        recommended_repair: d.recommended_repair,
        recommended_price: parseFloat(d.recommended_price),
      });
      toast.success("Diagnose sendt til kunde");
      setDiagDraft((draft) => ({ ...draft, [o.id]: undefined }));
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const updateDraft = (orderId, field, value) =>
    setDiagDraft((d) => ({ ...d, [orderId]: { ...(d[orderId] || {}), [field]: value } }));

  const earnings = orders
    .filter((o) => o.status === "completed" || o.status === "paid_out" || o.status === "sent_back")
    .reduce((s, o) => s + (o.service_fee || 0), 0);

  const active = orders.filter((o) => !DONE_STATUSES.has(o.status));
  const completedCount = orders.filter((o) => DONE_STATUSES.has(o.status)).length;

  const diagnosticOrders = orders.filter((o) => o.is_diagnostic);
  const diagActive = diagnosticOrders.filter((o) => !DIAG_COMPLETED_STATUSES.has(o.status));
  const diagDone = diagnosticOrders.filter((o) => DIAG_COMPLETED_STATUSES.has(o.status));
  const diagList = diagTab === "aktive" ? diagActive : diagDone;

  return (
    <div className="tk-container py-16" data-testid="fixer-dashboard">
      <p className="tk-overline mb-3">Verksted-dashbord</p>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-2">
        {profile?.display_name || "Mitt verksted"}
      </h1>
      <p className="text-sm text-slate-500 mb-10">
        {profile?.address}, {profile?.postal_code}
        {profile && (
          <span
            className={`ml-3 inline-block text-xs px-2 py-0.5 rounded ${
              profile.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
            }`}
          >
            {profile.active ? "Tilgjengelig" : "Utilgjengelig"}
          </span>
        )}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <Stat label="Aktive oppdrag" value={active.length} />
        <Stat label="Fullført" value={completedCount} />
        <Stat label="Opptjent" value={`kr ${earnings.toFixed(0)}`} />
      </div>

      <div className="mb-12">
        <Link
          to="/verksted/oppdrag"
          className="tk-btn-primary"
          data-testid="goto-jobs-btn"
        >
          Se alle oppdrag →
        </Link>
      </div>

      {/* Diagnose-kø with 2 tabs */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Stethoscope size={20} className="text-[#4A7C59]" /> Diagnose-kø
        </h2>
        <div className="flex gap-2 mb-6" data-testid="diag-tab-bar">
          {[
            ["aktive", `Aktive (${diagActive.length})`],
            ["fullfort", `Fullført (${diagDone.length})`],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setDiagTab(k)}
              className={`px-3 py-1.5 rounded-md text-xs border ${
                diagTab === k
                  ? "bg-[#0B1C30] text-white border-[#0B1C30]"
                  : "border-[#E2E8F0] bg-white text-slate-700"
              }`}
              data-testid={`diag-tab-${k}`}
            >
              {l}
            </button>
          ))}
        </div>

        {diagList.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="diag-empty">
            {diagTab === "aktive"
              ? "Ingen aktive diagnoser akkurat nå."
              : "Ingen fullførte diagnoser ennå."}
          </p>
        ) : (
          <div className="space-y-4" data-testid="fixer-diagnostic-queue">
            {diagList.map((o) => (
              <div key={o.id} className="tk-card" data-testid={`diag-order-${o.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
                  <div>
                    <Link
                      to={`/verksted/jobb/${o.id}`}
                      className="text-xs text-slate-400 hover:text-[#0B1C30] font-mono"
                    >
                      #{o.id.slice(0, 8)} ↗
                    </Link>
                    <h3 className="font-semibold text-lg leading-snug mt-1">
                      {o.product_name}
                    </h3>
                    {o.symptoms?.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        Symptomer: {o.symptoms.join(", ")}
                      </p>
                    )}
                  </div>
                  <span className="text-xs px-2 py-1 rounded-md bg-slate-100">
                    {DIAG_STATUS_LABEL[o.status] || o.status}
                  </span>
                </div>

                {o.status === "assigned" && (
                  <div
                    className="mt-4 pt-4 border-t border-[#E2E8F0] space-y-3"
                    data-testid={`inline-diag-form-${o.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-[#4A7C59]" />
                      <span className="font-semibold text-sm">Fyll inn diagnose</span>
                    </div>
                    <textarea
                      placeholder="Funn"
                      className="tk-input min-h-[70px]"
                      value={diagDraft[o.id]?.findings || ""}
                      onChange={(e) => updateDraft(o.id, "findings", e.target.value)}
                      data-testid={`diag-findings-${o.id}`}
                    />
                    <input
                      placeholder="Anbefalt reparasjon"
                      className="tk-input"
                      value={diagDraft[o.id]?.recommended_repair || ""}
                      onChange={(e) => updateDraft(o.id, "recommended_repair", e.target.value)}
                      data-testid={`diag-recommended-${o.id}`}
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Tilleggspris (NOK)"
                      className="tk-input max-w-[200px]"
                      value={diagDraft[o.id]?.recommended_price || ""}
                      onChange={(e) => updateDraft(o.id, "recommended_price", e.target.value)}
                      data-testid={`diag-price-${o.id}`}
                    />
                    <button
                      onClick={() => submitDiagnosis(o)}
                      className="tk-btn-primary !py-2 !px-4 text-sm"
                      data-testid={`diag-submit-${o.id}`}
                    >
                      Send diagnose til kunde
                    </button>
                  </div>
                )}

                {(o.status === "diagnosis_ready" ||
                  o.status === "approved_awaiting_extra_payment" ||
                  DIAG_COMPLETED_STATUSES.has(o.status)) &&
                  o.diagnostic_findings && (
                    <div className="mt-4 pt-4 border-t border-[#E2E8F0] text-sm space-y-1">
                      <p>
                        <span className="text-slate-500">Funn:</span> {o.diagnostic_findings}
                      </p>
                      <p>
                        <span className="text-slate-500">Anbefalt:</span> {o.recommended_repair}
                      </p>
                      <p>
                        <span className="text-slate-500">Tilleggspris:</span> kr{" "}
                        {Number(o.recommended_price || 0).toFixed(0)}
                      </p>
                      {DIAG_COMPLETED_STATUSES.has(o.status) && (
                        <p className="flex items-center gap-1 text-emerald-700 text-xs mt-2">
                          <CheckCircle2 size={14} /> Saken er avsluttet
                        </p>
                      )}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="tk-card">
      <p className="tk-overline mb-2">{label}</p>
      <p className="text-3xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}
